import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Download,
  Loader2,
  Lock,
  Plus,
  Trash2,
  TrendingDown,
  TrendingUp,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { PAIRS, pipsBetween, pipValuePerLot } from "@/lib/mock-signals";
import {
  closeJournalEntry,
  createJournalEntry,
  deleteJournalEntry,
  listMyJournal,
  listRecentSignalsForJournal,
} from "@/lib/journal.functions";

export const Route = createFileRoute("/_authenticated/journal")({
  component: JournalPage,
});

type JournalEntry = {
  id: string;
  pair: string;
  direction: "BUY" | "SELL";
  entry_price: number;
  exit_price: number | null;
  stop_loss: number | null;
  take_profit: number | null;
  lot_size: number;
  entry_time: string;
  exit_time: string | null;
  pips_result: number | null;
  pnl_usd: number | null;
  outcome: "WIN" | "LOSS" | "BREAKEVEN" | null;
  linked_signal_id: string | null;
  notes: string | null;
  is_open: boolean;
  created_at: string;
};

function nowLocalInput(): string {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

function calcPnl(
  pair: string,
  direction: "BUY" | "SELL",
  entry: number,
  exit: number,
  lots: number,
) {
  const raw = direction === "BUY" ? exit - entry : entry - exit;
  const pips = pipsBetween(pair, 0, Math.abs(raw)) * (raw >= 0 ? 1 : -1);
  const pnl = pips * pipValuePerLot(pair) * lots;
  return { pips: +pips.toFixed(1), pnl: +pnl.toFixed(2) };
}

function JournalPage() {
  const qc = useQueryClient();
  const fetchJournal = useServerFn(listMyJournal);
  const fetchSignals = useServerFn(listRecentSignalsForJournal);

  const { data, isLoading } = useQuery({
    queryKey: ["journal"],
    queryFn: () => fetchJournal(),
  });
  const { data: signalsData } = useQuery({
    queryKey: ["journal-signals"],
    queryFn: () => fetchSignals(),
  });

  const entries = (data?.entries ?? []) as JournalEntry[];
  const plan = data?.plan ?? "free";
  const limit = data?.limit;
  const used = data?.used ?? 0;
  const limitReached = limit != null && used >= limit;

  const stats = useMemo(() => computeStats(entries), [entries]);

  const [openLog, setOpenLog] = useState(false);
  const [closeTarget, setCloseTarget] = useState<JournalEntry | null>(null);

  const removeMut = useMutation({
    mutationFn: useServerFn(deleteJournalEntry),
    onSuccess: () => {
      toast.success("Entry deleted");
      qc.invalidateQueries({ queryKey: ["journal"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const exportCsv = () => {
    if (plan !== "elite") {
      toast.error("CSV export is an Elite feature");
      return;
    }
    const header = [
      "pair",
      "direction",
      "entry_price",
      "exit_price",
      "stop_loss",
      "take_profit",
      "lot_size",
      "entry_time",
      "exit_time",
      "pips_result",
      "pnl_usd",
      "outcome",
      "notes",
    ];
    const lines = [header.join(",")];
    for (const e of entries) {
      lines.push(
        [
          e.pair,
          e.direction,
          e.entry_price,
          e.exit_price ?? "",
          e.stop_loss ?? "",
          e.take_profit ?? "",
          e.lot_size,
          e.entry_time,
          e.exit_time ?? "",
          e.pips_result ?? "",
          e.pnl_usd ?? "",
          e.outcome ?? "",
          (e.notes ?? "").replaceAll('"', '""').replaceAll(",", " "),
        ].join(","),
      );
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `forexmind-journal-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main className="min-w-0 flex-1 p-3 sm:p-4 lg:p-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 font-display text-2xl font-bold">
            <BookOpen className="h-6 w-6 text-primary" />
            Trade Journal
          </h1>
          <p className="text-sm text-muted-foreground">
            Track every trade. Prove the edge. Beat the churn.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {limit != null && (
            <span className="rounded-md border border-border/60 bg-card/40 px-2 py-1 font-mono text-[11px] text-muted-foreground">
              {used}/{limit} entries · {plan.toUpperCase()}
            </span>
          )}
          {limit == null && (
            <span className="rounded-md border border-warn/40 bg-warn/10 px-2 py-1 font-mono text-[11px] text-warn">
              UNLIMITED · ELITE
            </span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={plan !== "elite" || entries.length === 0}
            className="gap-2"
          >
            {plan === "elite" ? <Download className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
            CSV
          </Button>
          <Button
            size="sm"
            onClick={() => setOpenLog(true)}
            disabled={limitReached}
            className="gap-2"
          >
            <Plus className="h-4 w-4" /> Log trade
          </Button>
        </div>
      </div>

      {limitReached && (
        <Card className="mb-4 border-warn/40 bg-warn/5 p-3 text-sm text-warn">
          You've hit the {limit}-entry limit on the {plan.toUpperCase()} plan. Upgrade to keep
          journaling.
        </Card>
      )}

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <StatCard label="Trades" value={String(stats.total)} />
        <StatCard
          label="Win rate"
          value={stats.closed ? `${stats.winRate.toFixed(1)}%` : "—"}
          tone={stats.winRate >= 50 ? "buy" : "sell"}
        />
        <StatCard
          label="Total pips"
          value={stats.totalPips.toFixed(1)}
          tone={stats.totalPips >= 0 ? "buy" : "sell"}
        />
        <StatCard
          label="Total P&L"
          value={`$${stats.totalPnl.toFixed(2)}`}
          tone={stats.totalPnl >= 0 ? "buy" : "sell"}
        />
        <StatCard label="Best pair" value={stats.bestPair ?? "—"} tone="buy" />
        <StatCard label="Worst pair" value={stats.worstPair ?? "—"} tone="sell" />
      </div>

      <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Open" value={String(stats.open)} />
        <StatCard label="Closed" value={String(stats.closed)} />
        <StatCard
          label="Streak"
          value={
            stats.streak === 0
              ? "—"
              : `${stats.streak > 0 ? "W" : "L"}${Math.abs(stats.streak)}`
          }
          tone={stats.streak >= 0 ? "buy" : "sell"}
        />
        <StatCard label="Avg R:R" value={stats.avgRR ? stats.avgRR.toFixed(2) : "—"} />
      </div>

      <Card className="min-w-0 overflow-hidden">
        <div className="border-b border-border/60 p-3 text-sm font-semibold">Entries</div>
        {isLoading ? (
          <div className="flex items-center justify-center p-8 text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            No trades yet. Click <span className="text-foreground">Log trade</span> to start.
          </div>
        ) : (
          <div className="max-w-full overflow-x-auto">
            <table className="w-full min-w-[760px] text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 py-2 text-left">Pair</th>
                  <th className="px-3 py-2 text-left">Dir</th>
                  <th className="px-3 py-2 text-right">Entry</th>
                  <th className="px-3 py-2 text-right">Exit</th>
                  <th className="px-3 py-2 text-right">Lots</th>
                  <th className="px-3 py-2 text-right">Pips</th>
                  <th className="px-3 py-2 text-right">P&L</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const tone =
                    e.outcome === "WIN"
                      ? "text-buy"
                      : e.outcome === "LOSS"
                        ? "text-sell"
                        : "text-muted-foreground";
                  return (
                    <tr key={e.id} className="border-t border-border/40 hover:bg-accent/20">
                      <td className="whitespace-nowrap px-3 py-2 font-mono text-xs">
                        {new Date(e.entry_time).toLocaleDateString()}
                      </td>
                      <td className="px-3 py-2 font-mono">{e.pair}</td>
                      <td className="px-3 py-2">
                        <span
                          className={
                            e.direction === "BUY"
                              ? "rounded bg-buy/10 px-1.5 py-0.5 text-[11px] font-bold text-buy"
                              : "rounded bg-sell/10 px-1.5 py-0.5 text-[11px] font-bold text-sell"
                          }
                        >
                          {e.direction}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{e.entry_price}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {e.exit_price ?? "—"}
                      </td>
                      <td className="px-3 py-2 text-right font-mono">{e.lot_size}</td>
                      <td className={`px-3 py-2 text-right font-mono ${tone}`}>
                        {e.pips_result != null ? e.pips_result.toFixed(1) : "—"}
                      </td>
                      <td className={`px-3 py-2 text-right font-mono ${tone}`}>
                        {e.pnl_usd != null ? `$${e.pnl_usd.toFixed(2)}` : "—"}
                      </td>
                      <td className="px-3 py-2">
                        {e.is_open ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-xs"
                            onClick={() => setCloseTarget(e)}
                          >
                            Close
                          </Button>
                        ) : (
                          <span className={`text-xs font-semibold ${tone}`}>{e.outcome}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7"
                          onClick={() => {
                            if (confirm("Delete this entry?")) removeMut.mutate({ data: { id: e.id } });
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <LogTradeDialog
        open={openLog}
        onClose={() => setOpenLog(false)}
        signals={signalsData?.signals ?? []}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["journal"] });
          setOpenLog(false);
        }}
      />
      <CloseTradeDialog
        entry={closeTarget}
        onClose={() => setCloseTarget(null)}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["journal"] });
          setCloseTarget(null);
        }}
      />
    </main>
  );
}

function StatCard({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: "buy" | "sell";
}) {
  const color = tone === "buy" ? "text-buy" : tone === "sell" ? "text-sell" : "text-foreground";
  return (
    <Card className="min-w-0 p-3">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`mt-1 truncate font-mono text-lg font-bold ${color}`}>{value}</div>
    </Card>
  );
}

function LogTradeDialog({
  open,
  onClose,
  signals,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  signals: Array<{ id: string; pair: string; action: string; entry: number | null; created_at: string }>;
  onSaved: () => void;
}) {
  const [pair, setPair] = useState<string>("EURUSD");
  const [direction, setDirection] = useState<"BUY" | "SELL">("BUY");
  const [entry, setEntry] = useState("");
  const [sl, setSl] = useState("");
  const [tp, setTp] = useState("");
  const [lots, setLots] = useState("0.10");
  const [entryTime, setEntryTime] = useState<string>(nowLocalInput());
  const [linkedId, setLinkedId] = useState<string>("none");
  const [notes, setNotes] = useState("");

  const createMut = useMutation({
    mutationFn: useServerFn(createJournalEntry),
    onSuccess: () => {
      toast.success("Trade logged");
      onSaved();
      setEntry("");
      setSl("");
      setTp("");
      setNotes("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const submit = () => {
    const entryNum = parseFloat(entry);
    const lotsNum = parseFloat(lots);
    if (!entryNum || !lotsNum) {
      toast.error("Entry price and lot size are required");
      return;
    }
    createMut.mutate({
      data: {
        pair,
        direction,
        entry_price: entryNum,
        stop_loss: sl ? parseFloat(sl) : null,
        take_profit: tp ? parseFloat(tp) : null,
        lot_size: lotsNum,
        entry_time: new Date(entryTime).toISOString(),
        linked_signal_id: linkedId !== "none" ? linkedId : null,
        notes: notes || null,
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Log a trade</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pair">
            <Select value={pair} onValueChange={setPair}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAIRS.map((p) => (
                  <SelectItem key={p} value={p}>{p}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Direction">
            <Select value={direction} onValueChange={(v) => setDirection(v as "BUY" | "SELL")}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="BUY">BUY</SelectItem>
                <SelectItem value="SELL">SELL</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Entry price">
            <Input type="number" step="any" value={entry} onChange={(e) => setEntry(e.target.value)} />
          </Field>
          <Field label="Lot size">
            <Input type="number" step="0.01" value={lots} onChange={(e) => setLots(e.target.value)} />
          </Field>
          <Field label="Stop loss">
            <Input type="number" step="any" value={sl} onChange={(e) => setSl(e.target.value)} />
          </Field>
          <Field label="Take profit">
            <Input type="number" step="any" value={tp} onChange={(e) => setTp(e.target.value)} />
          </Field>
          <Field label="Entry time" className="col-span-2">
            <Input type="datetime-local" value={entryTime} onChange={(e) => setEntryTime(e.target.value)} />
          </Field>
          <Field label="Linked ForexMind signal (optional)" className="col-span-2">
            <Select value={linkedId} onValueChange={setLinkedId}>
              <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {signals.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.pair} · {s.action} · {new Date(s.created_at).toLocaleDateString()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Notes" className="col-span-2">
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}><X className="h-4 w-4" /> Cancel</Button>
          <Button onClick={submit} disabled={createMut.isPending}>
            {createMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Save trade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CloseTradeDialog({
  entry,
  onClose,
  onSaved,
}: {
  entry: JournalEntry | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [exitPrice, setExitPrice] = useState("");
  const [exitTime, setExitTime] = useState(nowLocalInput());

  const closeMut = useMutation({
    mutationFn: useServerFn(closeJournalEntry),
    onSuccess: () => {
      toast.success("Trade closed");
      onSaved();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const preview = useMemo(() => {
    if (!entry) return null;
    const ex = parseFloat(exitPrice);
    if (!ex) return null;
    const { pips, pnl } = calcPnl(entry.pair, entry.direction, entry.entry_price, ex, entry.lot_size);
    const outcome: "WIN" | "LOSS" | "BREAKEVEN" =
      Math.abs(pips) < 0.5 ? "BREAKEVEN" : pips > 0 ? "WIN" : "LOSS";
    return { pips, pnl, outcome };
  }, [entry, exitPrice]);

  if (!entry) return null;

  const submit = () => {
    if (!preview) {
      toast.error("Enter an exit price");
      return;
    }
    closeMut.mutate({
      data: {
        id: entry.id,
        exit_price: parseFloat(exitPrice),
        exit_time: new Date(exitTime).toISOString(),
        outcome: preview.outcome,
        pips_result: preview.pips,
        pnl_usd: preview.pnl,
      },
    });
  };

  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            Close {entry.pair} {entry.direction} @ {entry.entry_price}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Exit price">
            <Input type="number" step="any" value={exitPrice} onChange={(e) => setExitPrice(e.target.value)} />
          </Field>
          <Field label="Exit time">
            <Input type="datetime-local" value={exitTime} onChange={(e) => setExitTime(e.target.value)} />
          </Field>
        </div>
        {preview && (
          <Card className="p-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Outcome</span>
              <span
                className={
                  preview.outcome === "WIN"
                    ? "flex items-center gap-1 font-bold text-buy"
                    : preview.outcome === "LOSS"
                      ? "flex items-center gap-1 font-bold text-sell"
                      : "font-bold text-muted-foreground"
                }
              >
                {preview.outcome === "WIN" && <TrendingUp className="h-4 w-4" />}
                {preview.outcome === "LOSS" && <TrendingDown className="h-4 w-4" />}
                {preview.outcome}
              </span>
            </div>
            <div className="mt-1 flex items-center justify-between font-mono text-sm">
              <span>{preview.pips.toFixed(1)} pips</span>
              <span className={preview.pnl >= 0 ? "text-buy" : "text-sell"}>
                ${preview.pnl.toFixed(2)}
              </span>
            </div>
          </Card>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={closeMut.isPending || !preview}>
            {closeMut.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Close trade
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={`space-y-1 ${className ?? ""}`}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}

function computeStats(entries: JournalEntry[]) {
  const closed = entries.filter((e) => !e.is_open && e.outcome);
  const wins = closed.filter((e) => e.outcome === "WIN").length;
  const totalPips = closed.reduce((s, e) => s + (e.pips_result ?? 0), 0);
  const totalPnl = closed.reduce((s, e) => s + (e.pnl_usd ?? 0), 0);

  const byPair = new Map<string, number>();
  for (const e of closed) {
    byPair.set(e.pair, (byPair.get(e.pair) ?? 0) + (e.pnl_usd ?? 0));
  }
  let bestPair: string | null = null;
  let worstPair: string | null = null;
  let bestVal = -Infinity;
  let worstVal = Infinity;
  for (const [p, v] of byPair) {
    if (v > bestVal) { bestVal = v; bestPair = p; }
    if (v < worstVal) { worstVal = v; worstPair = p; }
  }

  // Streak: positive = win streak, negative = loss streak
  const sortedClosed = [...closed].sort(
    (a, b) => new Date(b.exit_time ?? b.entry_time).getTime() - new Date(a.exit_time ?? a.entry_time).getTime(),
  );
  let streak = 0;
  for (const e of sortedClosed) {
    if (e.outcome === "WIN") {
      if (streak < 0) break;
      streak += 1;
    } else if (e.outcome === "LOSS") {
      if (streak > 0) break;
      streak -= 1;
    } else {
      break;
    }
  }

  const rrValues = closed
    .map((e) => {
      if (!e.stop_loss || !e.take_profit) return null;
      const risk = Math.abs(e.entry_price - e.stop_loss);
      const reward = Math.abs(e.take_profit - e.entry_price);
      return risk > 0 ? reward / risk : null;
    })
    .filter((x): x is number => x != null);
  const avgRR = rrValues.length ? rrValues.reduce((a, b) => a + b, 0) / rrValues.length : 0;

  return {
    total: entries.length,
    open: entries.filter((e) => e.is_open).length,
    closed: closed.length,
    winRate: closed.length ? (wins / closed.length) * 100 : 0,
    totalPips,
    totalPnl,
    bestPair: bestPair && bestVal > 0 ? bestPair : null,
    worstPair: worstPair && worstVal < 0 ? worstPair : null,
    streak,
    avgRR,
  };
}
